import { useCallback, useEffect, useReducer, useRef } from "react"

import type { AnimationEvent, AnimationPlayer, PitchSnapshot } from "@/types"

import {
  applyEventToPitchState,
  buildPitchStateFromSnapshot,
} from "../replay/pitch-helpers"
import type { PitchState } from "../replay/use-pitch-state"

// ─── Types ──────────────────────────────────────────────────────────────────

export interface MiniReplayState {
  pitchState: PitchState
  highlightType: "touchdown" | "turnover" | "kickoff" | null
  eventKey: number
  progress: number
  eventIndex: number
  /** Countdown ticks remaining before loop reset. -1 = not holding. */
  holdRemaining: number
}

/** Number of extra ticks to hold on the final event before looping. */
const HOLD_TICKS = 3

export type MiniReplayAction =
  | {
      type: "TICK"
      events: AnimationEvent[]
      players: Record<string, AnimationPlayer>
      snapshot: PitchSnapshot
    }
  | { type: "RESET"; initialPitchState: PitchState; startIndex: number }

// ─── Pure reducer ───────────────────────────────────────────────────────────

export function miniReplayReducer(
  state: MiniReplayState,
  action: MiniReplayAction,
): MiniReplayState {
  switch (action.type) {
    case "TICK": {
      const { events, players, snapshot } = action
      const nextIdx = state.eventIndex

      // Holding on final event before loop reset
      if (state.holdRemaining > 0) {
        return { ...state, holdRemaining: state.holdRemaining - 1 }
      }

      if (nextIdx >= events.length) {
        if (state.holdRemaining === 0) {
          // Hold period just ended — reset now
          return {
            pitchState: buildPitchStateFromSnapshot(snapshot, players),
            highlightType: null,
            eventKey: state.eventKey,
            progress: 0,
            eventIndex: 0,
            holdRemaining: -1,
          }
        }
        // Start holding
        return { ...state, holdRemaining: HOLD_TICKS }
      }

      const event = events[nextIdx]
      return {
        pitchState: applyEventToPitchState(state.pitchState, event, players),
        highlightType: event.highlightType ?? null,
        eventKey: state.eventKey + 1,
        progress: (nextIdx + 1) / events.length,
        eventIndex: nextIdx + 1,
        holdRemaining: -1,
      }
    }

    case "RESET": {
      return {
        pitchState: action.initialPitchState,
        highlightType: null,
        eventKey: 0,
        progress: 0,
        eventIndex: action.startIndex,
        holdRemaining: -1,
      }
    }
  }
}

// ─── Derived state ──────────────────────────────────────────────────────────

export interface MiniReplayDerived {
  ballPosition: [number, number] | null
}

export function computeDerivedState(state: MiniReplayState): MiniReplayDerived {
  return {
    ballPosition: findBallPosition(state.pitchState),
  }
}

function findBallPosition(pitchState: PitchState): [number, number] | null {
  // Only return a position for a loose ball — the player token's hasBall dot
  // already indicates ball carriers, so we don't need a second indicator.
  return pitchState.ballPosition
}

// ─── Initial state builder ──────────────────────────────────────────────────

export function buildInitialMiniReplayState(
  snapshot: PitchSnapshot,
  events: AnimationEvent[],
  players: Record<string, AnimationPlayer>,
): MiniReplayState {
  const base = buildPitchStateFromSnapshot(snapshot, players)
  if (events.length === 0) {
    return {
      pitchState: base,
      highlightType: null,
      eventKey: 0,
      progress: 0,
      eventIndex: 0,
      holdRemaining: -1,
    }
  }

  const pitchState = applyEventToPitchState(base, events[0], players)
  const startIndex = 1
  return {
    pitchState,
    highlightType: events[0].highlightType ?? null,
    eventKey: 0,
    progress: startIndex / events.length,
    eventIndex: startIndex,
    holdRemaining: -1,
  }
}

// ─── Hook wrapper ───────────────────────────────────────────────────────────

const EVENT_INTERVAL_MS = 800

export function useMiniReplayReducer(
  replaySnapshot: PitchSnapshot,
  replayEvents: AnimationEvent[],
  playersData: Record<string, AnimationPlayer>,
  paused: boolean,
) {
  const [state, dispatch] = useReducer(
    miniReplayReducer,
    { replaySnapshot, replayEvents, playersData },
    ({ replaySnapshot: snap, replayEvents: evts, playersData: pls }) =>
      buildInitialMiniReplayState(snap, evts, pls),
  )

  const intervalRef = useRef<number>(0)

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = 0
    }
  }, [])

  const startTimer = useCallback(() => {
    clearTimer()
    if (replayEvents.length === 0) return

    intervalRef.current = window.setInterval(() => {
      dispatch({
        type: "TICK",
        events: replayEvents,
        players: playersData,
        snapshot: replaySnapshot,
      })
    }, EVENT_INTERVAL_MS)
  }, [replayEvents, replaySnapshot, playersData, clearTimer])

  useEffect(() => {
    if (paused) {
      clearTimer()
    } else {
      startTimer()
    }
    return clearTimer
  }, [paused, startTimer, clearTimer])

  const derived = computeDerivedState(state)

  return { state, derived }
}
